import { AuthFetchWithResponse } from 'src/Types/api'
import { AccessRight } from '../../types/access-right'
import { ApiResult, throwApiError } from 'src/Libs/api'
import { StatusCodes } from '@e3dc-react/shell/Libs'
import { AuthFetchError } from '@e3dc-react/shell/Types'

export interface AccountLinkDto {
  id: number
  installationId: number
  accountId: string
  accountType: 'user' | 'company' | 'cloud'
  accountSource: 'e3dc' | 'hager'
  role: 'owner' | 'installer' | 'user' | 'sales'
  inviteId: string
  startAt: string
  stopAt: string
  permissionType: AccessRight
  storagePermissionType: AccessRight
  endUserPermissionType: AccessRight
  eMobilityPermissionType: AccessRight
  featureActivationPermissionType: AccessRight
  /** ISO date */
  decommissionedAt: string
  /** ISO date */
  createdAt: string
  /** ISO date */
  updatedAt: string
  userName: string
  lastName: string
  firstName: string
  companyName: string
}

export const getAccountLinks = async (
  installationId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<ArrayType<Nullable<AccountLinkDto>> | null>> => {
  try {
    const response = await authFetch<ArrayType<Nullable<AccountLinkDto>>>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/account-links`,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<ArrayType<Nullable<AccountLinkDto>>>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to get account links:')
  }
}

export interface AccountLinkPUTDto {
  /** Advanced Settings access*/
  permissionType: AccessRight
  /** Dashboard access*/
  storagePermissionType: AccessRight
  /** End-user Settings access*/
  endUserPermissionType: AccessRight
  /** E-mobility access*/
  eMobilityPermissionType: AccessRight
}

/** Update user permissions. */
export const updateAccountLink = async (
  installationId: number,
  accountId: string,
  payload: AccountLinkPUTDto,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/account-links/${accountId}`,
      method: 'PUT',
      payload,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return throwApiError(error, 'Failed to update account link:')
  }
}

/** Removes account link from installation. Does NOT delete the account itself. */
export const deleteAccountLink = async (installationId: number, accountId: string, authFetch: AuthFetchWithResponse): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/account-links/${accountId}`,
      method: 'DELETE',
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.NO_CONTENT) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return throwApiError(error, 'Failed to delete account link:')
  }
}
